import {useState, useEffect, useContext} from 'react';
import {useIntl} from "react-intl";
import {useWorksiteData} from "./useWorksiteData";
import {buildServicesSurveysDocuments} from "@pages/WorksitesDocuments/utils/buildServicesSurveysDocuments";
import {buildWorksiteImagesAndNotesAttachments} from "@pages/WorksitesDocuments/utils/buildWorksiteImagesAndNotesAttachments";
import {buildTaskImagesAndReports} from "@pages/WorksitesDocuments/utils/buildTaskImagesAndReports";
import {buildProductsDocuments} from "@pages/WorksitesDocuments/utils/buildProductsDocuments";
import {buildUsersDocuments} from "@pages/WorksitesDocuments/utils/buildUsersDocuments";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import useWorksiteTaskPdfGenerator from "./useWorksiteTaskPdfGenerator";
import {useDesignReportsDownloader} from "@feature/surveys/hooks/useDesignReportsDownloader";
import {NotificationsContext} from "@ui-components/Notifications";
import {prodGrouping} from "@pages/WorksitesDocuments/utils/constants";
import CountryContext from "../CountryContext";
import { buildQuotationsAttachments } from 'pages/WorksitesDocuments/utils/buildQuotationsAttachments';
import {useQuotations} from "../feature/quotations/hooks/useQuotations";
import authenticationService from "../services/AuthenticationService";

export const useWorksiteDocumentsAndData = (
	worksite
) => {

	const intl = useIntl();
	const {push} = useContext(NotificationsContext);
	const { country } = useContext(CountryContext);
	const countryApiRequest = useCountryAxiosInstance();
	const {downloadQuotationPdf} = useQuotations();
	const userData = authenticationService.getUserData();

	const {
		notesAttachments,
		servicesSurveys,
		taskImages,
		tasks,
		products,
		userLoadedDocuments,
		setUserLoadedDocuments,
		moveUserDocument,
		sharing,
		creators,
		worksiteUsers,
		quotationsAttachments
	} = useWorksiteData(worksite);
	// array of ids of survey chosen for worksite pdf report generation
	const [chosenSurveys, setChosenSurveys] = useState([]);
	// boolean used to show the dialog to select the surveys for the worksite pdf report
	const [
		showReportsSelectionWorksiteDialog,
		setShowReportsSelectionWorksiteDialog,
	] = useState(false);
	const [reportDialogCallback, setReportDialogCallback] = useState(undefined);
	/*
	used to show the dialog to edit a group of users documents, if null
	the dialog is not shown, otherwise it contains the group to edit
	 */
	const [showEditGroup, setShowEditGroup] = useState(null);
	const [showEditDocument, setShowEditDocument] = useState(null);
	// boolean used for tasks pdf report generation
	const [downloadingTask, setDownloadingTask] = useState(false);
	// function to dispatch the pdf download of a task
	const dispatchPDFDownload = useWorksiteTaskPdfGenerator(downloadingTask, setDownloadingTask);
	// hook to download design surveys pdf
	const {
		available: availableDesignSurveys,
		download: downloadDesignSurveysPdf,
	} = useDesignReportsDownloader(worksite.id);
	/*
	state to store the documents to be shown in the worksite documents page
	this state is an object with the following structure:
	{
		sectionKey: {
			title,
			actions,
			alternateColor,
			groups: [
				{
					key,
					title,
					documents: [
						{
							name,
							onDocumentClicked,
							...other document properties depending on the document type
						}
					]
				}
			],
	 */
	const [documents, setDocuments] = useState({});
	// state to store the products documents grouping
	const [productsDocumentsGrouping, setProductsDocumentsGrouping] = useState(prodGrouping.DOCUMENT_TYPE);
	// state to store the delete user documents dialog
	const [showDeleteUserDocumentsDialog, setShowDeleteUserDocumentsDialog] = useState(null);

	const [search, setSearch] = useState("")

	useEffect(() => {
		setDocuments((prev) => ({
			...prev,
			...buildServicesSurveysDocuments(
				worksite,
				servicesSurveys,
				worksiteUsers,
				setChosenSurveys,
				setShowReportsSelectionWorksiteDialog,
				setReportDialogCallback,
				downloadDesignSurveysPdf,
				intl,
				availableDesignSurveys,
				userData
			),
		}));
	}, [servicesSurveys, creators, sharing, worksiteUsers, availableDesignSurveys]);

	useEffect(() => {
		setDocuments((prev) => ({
			...prev,
			worksite_images_and_notes_attachments: buildWorksiteImagesAndNotesAttachments(
				worksite,
				notesAttachments,
				worksiteUsers,
				intl,
				countryApiRequest
			)
		}));
	}, [notesAttachments, worksiteUsers]);

	useEffect(() => {
		setDocuments((prev) => ({
			...prev,
			tasks_images_and_reports: buildTaskImagesAndReports(
				worksite,
				tasks,
				taskImages,
				worksiteUsers,
				dispatchPDFDownload,
				intl,
				countryApiRequest
			)
		}));
	}, [tasks, taskImages, worksiteUsers]);

	useEffect(() => {
		setDocuments((prev) => ({
			...prev,
			products: buildProductsDocuments(
				products,
				intl,
				countryApiRequest,
				productsDocumentsGrouping,
				setProductsDocumentsGrouping,
				search
			)
		}));
	}, [products, productsDocumentsGrouping, search]);

	useEffect(() => {
		setDocuments((prev) => ({
			...prev,
			users_documents: buildUsersDocuments(
        worksite.id,
				userLoadedDocuments,
				setUserLoadedDocuments,
				setShowDeleteUserDocumentsDialog,
				intl,
				setShowEditGroup,
				setShowEditDocument,
				countryApiRequest,
				push,
				country,
			)
		}));
	}, [userLoadedDocuments]);

	useEffect(() => {
		if (quotationsAttachments) {
			setDocuments((prev) => ({
				...prev,
				quotations_attachments: buildQuotationsAttachments(
					quotationsAttachments,
					intl,
					countryApiRequest,
					downloadQuotationPdf
				)
			}));
		}
	}, [quotationsAttachments]);

	return {
		worksite,
		documents,
		setDocuments,
		creators,
		sharing,
		servicesSurveys,
		chosenSurveys,
		showReportsSelectionWorksiteDialog,
		setShowReportsSelectionWorksiteDialog,
		reportDialogCallback,
		downloadDesignSurveysPdf,
		showEditGroup,
		setShowEditGroup,
		showEditDocument,
		setShowEditDocument,
		worksiteUsers,
		setUserLoadedDocuments,
		showDeleteUserDocumentsDialog,
		setShowDeleteUserDocumentsDialog,
		moveUserDocument,
		search,
		setSearch,
	}
}